import {
  mdiBarcode,
  mdiCash,
  mdiCashClock,
  mdiCashMinus,
  mdiCashRefund,
  mdiOpenInNew,
  mdiPrinterPosOutline,
  mdiTicketConfirmation
} from '@mdi/js';
import Icon from '@mdi/react';
import { Box, Fab, Grid, Tooltip, Typography } from '@mui/material';
import { format } from 'date-fns';
import { forwardRef } from 'react';

import { ReactComponent as CreditCard } from '../../assets/credit-card.svg';
import { ReactComponent as DebitCard } from '../../assets/debit-card.svg';
import { ReactComponent as PicPay } from '../../assets/picpay-logo.svg';
import { ReactComponent as Pix } from '../../assets/pix-logo.svg';
import { useUserContext } from '../../contexts/User.context';
import { TipoPagamento } from '../../models/TipoPagamento';
import { TIPO_TRANSACAO, TipoTransacao, Transacao } from '../../models/Transacao.model';
import { TipoUsuario } from '../../models/Usuario';
import { Colors } from '../../shared/colors';

import CpfCnpjFormatter from './CpfCnpjFormatter';
import Currency from './Currency';

export const getIcon = (tipo: TipoTransacao, _tipoPagamento?: TipoPagamento | number) => {
  if (tipo === TIPO_TRANSACAO.SALDO_ADICIONADO) return mdiCash;
  if (tipo === TIPO_TRANSACAO.COMPRA_PASSAGEM) return mdiCashMinus;
  if (tipo === TIPO_TRANSACAO.TRANSFERENCIA_ENVIADA) return mdiCashMinus;
  if (tipo === TIPO_TRANSACAO.TRANSFERENCIA_RECEBIDA) return mdiCash;
  if (tipo === TIPO_TRANSACAO.ESTORNO_PASSAGEM) return mdiCashRefund;
  if (tipo === TIPO_TRANSACAO.TRANSACAO_CREDITADA) return mdiCash;
  if (tipo === TIPO_TRANSACAO.TRANSFERENCIA_ESTORNADA) return mdiCashRefund;
  return mdiTicketConfirmation;
};

interface IconeProps {
  tipo: TipoTransacao;
  tipoPagamento: TipoPagamento | string | null | undefined;
  tipoPagamentoDescricao?: string | null;
}

const normalizarTextoPagamento = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const Icone = ({ tipo, tipoPagamento, tipoPagamentoDescricao }: IconeProps) => {
  const tipoPagamentoNumero = Number(tipoPagamento);

  switch (tipoPagamentoNumero) {
    case TipoPagamento.CartaoCredito:
    case TipoPagamento.YunoCredito:
      return <CreditCard />;
    case TipoPagamento.CartaoDebito:
    case TipoPagamento.YunoDebito:
      return <DebitCard />;
    case TipoPagamento.Pix:
    case TipoPagamento.YunoPix:
      return <Pix />;
    case TipoPagamento.PicPay:
      return <PicPay />;

    case TipoPagamento.PJPosPago:
      return <Icon size={1} color={Colors.primary.main} path={mdiCashClock} />;

    case TipoPagamento.Boleto:
      return <Icon size={1} color={Colors.primary.main} path={mdiBarcode} />;

    default:
      break;
  }

  const tipoPagamentoTexto = normalizarTextoPagamento(
    `${tipoPagamento ?? ''} ${tipoPagamentoDescricao ?? ''}`
  );

  if (tipoPagamentoTexto.includes('pix')) return <Pix />;
  if (tipoPagamentoTexto.includes('debito') || tipoPagamentoTexto.includes('debit'))
    return <DebitCard />;
  if (tipoPagamentoTexto.includes('credito') || tipoPagamentoTexto.includes('credit'))
    return <CreditCard />;
  if (tipoPagamentoTexto.includes('picpay')) return <PicPay />;
  if (tipoPagamentoTexto.includes('boleto'))
    return <Icon size={1} color={Colors.primary.main} path={mdiBarcode} />;

  return <Icon size={1} color={Colors.primary.main} path={getIcon(tipo, tipoPagamentoNumero)} />;
};

interface Props {
  transacao: Transacao;
  onClick: () => void;
  navigateToRecibo?: () => void;
  walletLayout?: boolean;
}

const CardTransacao = forwardRef<HTMLDivElement, Props>(
  ({ transacao, onClick, navigateToRecibo, walletLayout = false }, ref) => {
    const { userClaim } = useUserContext();
    const usuarioPF = userClaim === TipoUsuario.PESSOA_FISICA;
    const isHomePage = location.pathname === '/';
    const origemDestinoCpfCnpj =
      transacao.tipo !== TIPO_TRANSACAO.ESTORNO_PASSAGEM ? transacao.origemDestinoCpfCnpj : null;
    const temBilhetes = transacao.temBilhetes || (transacao.bilhetes?.length ?? 0) > 0;
    const temRecibo = transacao.tipoTransacaoDescricao == 'Saldo Adicionado';
    const transferenciaEnviada = transacao.tipo === TIPO_TRANSACAO.TRANSFERENCIA_ENVIADA;
    const temOrigemDestino = Boolean(transacao.origemDestinoNome || origemDestinoCpfCnpj);

    const getCurrencyColor = (tipo: TipoTransacao) => {
      if (tipo === TIPO_TRANSACAO.COMPRA_PASSAGEM) return Colors.error.red;

      if (tipo === TIPO_TRANSACAO.TRANSFERENCIA_ENVIADA) return Colors.error.red;

      if (tipo === TIPO_TRANSACAO.TRANSFERENCIA_ESTORNADA && usuarioPF) return Colors.error.red;

      if (tipo === TIPO_TRANSACAO.TRANSFERENCIA_ESTORNADA && !isHomePage) return Colors.error.red;

      return Colors.primary.dark;
    };

    if (walletLayout)
      return (
        <Grid ref={ref} item xs={12}>
          <Grid
            container
            paddingX={{ xs: 1.5, sm: 2 }}
            marginTop={{ xs: 2, sm: 2.25 }}
            paddingY={{ xs: 1.75, sm: 1.25 }}
            borderRadius={1}
            sx={{ bgcolor: Colors.white, boxShadow: '0 4px 14px rgba(16, 42, 86, 0.16)' }}
          >
            <Grid
              item
              container
              xs={12}
              wrap="nowrap"
              columnGap={{ xs: 1.5, sm: 2 }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
              <Grid item display="flex" alignItems="center" pt={{ xs: 0.5, sm: 0 }}>
                <Fab
                  size="small"
                  sx={{
                    border: 1,
                    borderColor: Colors.primary.main,
                    bgcolor: Colors.primary.light,
                    boxShadow: { xs: 1, sm: 3 },
                    flexShrink: 0
                  }}
                >
                  <Box display="flex" alignItems="center" p={1}>
                    <Icone
                      tipo={transacao.tipo}
                      tipoPagamento={transacao.tipoPagamento}
                      tipoPagamentoDescricao={transacao.tipoPagamentoDescricao}
                    />
                  </Box>
                </Fab>
              </Grid>

              <Grid
                item
                container
                flex={1}
                minWidth={0}
                columnSpacing={{ xs: 1, sm: 0 }}
                rowSpacing={{ xs: 1, sm: 0 }}
                alignItems={{ sm: 'center' }}
              >
                <Grid item container xs={12} sm={5} alignItems="center">
                  <Grid item display="flex" flexDirection="column" minWidth={0}>
                    <Typography
                      variant="body1"
                      color={Colors.text.primary}
                      fontWeight={700}
                      lineHeight={1.5}
                      sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, overflowWrap: 'anywhere' }}
                    >
                      {transacao.tipoTransacaoDescricao}
                    </Typography>
                    <Typography variant="caption" color={Colors.gray1}>
                      Autorização: {transacao.numeroAutorizacao}
                    </Typography>
                    {temBilhetes && (
                      <Box sx={{ cursor: 'pointer' }} onClick={onClick}>
                        <Typography variant="caption" color={Colors.primary.dark}>
                          Ver detalhes
                        </Typography>
                      </Box>
                    )}
                    {temRecibo && (
                      <Box
                        component="button"
                        type="button"
                        onClick={navigateToRecibo}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          width: 'fit-content',
                          p: 0,
                          border: 0,
                          bgcolor: 'transparent',
                          cursor: 'pointer'
                        }}
                      >
                        <Typography
                          component="span"
                          variant="caption"
                          color={Colors.primary.dark}
                          sx={{ textDecoration: 'underline', textUnderlineOffset: '2px' }}
                        >
                          Ver recibo
                        </Typography>
                        <Icon path={mdiOpenInNew} size={0.55} color={Colors.primary.dark} />
                      </Box>
                    )}
                  </Grid>
                </Grid>

                <Grid item container xs={7} sm={5} mt={{ xs: 0.25, sm: 0 }} minWidth={0}>
                  {temOrigemDestino && (
                    <Grid item xs={12} display="flex" flexDirection="column" minWidth={0}>
                      {transferenciaEnviada && (
                        <Typography
                          variant="caption"
                          color={Colors.gray1}
                          sx={{ fontSize: '11px', lineHeight: '18px' }}
                        >
                          Para
                        </Typography>
                      )}
                      <Tooltip title={transacao.origemDestinoNome ?? ''}>
                        <Typography
                          noWrap
                          variant="body1"
                          color={Colors.text.primary}
                          fontWeight={600}
                          lineHeight="21px"
                          sx={{ fontSize: { xs: '0.825rem', sm: '0.875rem' } }}
                        >
                          {transacao.origemDestinoNome}
                        </Typography>
                      </Tooltip>
                      {origemDestinoCpfCnpj && (
                        <Typography
                          variant="caption"
                          color={Colors.gray1}
                          sx={{ fontSize: '11px', lineHeight: '18px' }}
                        >
                          {origemDestinoCpfCnpj.length > 11 ? 'CNPJ: ' : 'CPF: '}
                          <CpfCnpjFormatter value={origemDestinoCpfCnpj} />
                        </Typography>
                      )}
                    </Grid>
                  )}
                </Grid>

                <Grid
                  item
                  xs={5}
                  sm={2}
                  mt={{ xs: 0.25, sm: 0 }}
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="flex-end"
                >
                  <Typography
                    fontWeight="bold"
                    color={getCurrencyColor(transacao.tipo)}
                    textAlign="right"
                    sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}
                  >
                    <Currency value={transacao.valor} />
                  </Typography>
                  <Typography
                    variant="caption"
                    color={Colors.gray1}
                    textAlign="right"
                    variantMapping={{ caption: 'p' }}
                    sx={{ textAlignLast: 'right' }}
                  >
                    {format(new Date(transacao.dataHora), 'dd/MM/yyyy HH:mm')}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      );

    return (
      <Grid ref={ref} item xs={12}>
        <Grid
          container
          border={1}
          paddingX={2}
          marginTop={2}
          paddingY={1.5}
          borderRadius={1}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ borderColor: Colors.primary.main }}
        >
          <Grid item container xs={12}>
            <Grid item>
              <Fab
                size="small"
                sx={{
                  border: 1,
                  borderColor: Colors.primary.main,
                  bgcolor: Colors.primary.light
                }}
              >
                <Box display="flex" alignItems="center" p={1}>
                  <Icone
                    tipo={transacao.tipo}
                    tipoPagamento={transacao.tipoPagamento}
                    tipoPagamentoDescricao={transacao.tipoPagamentoDescricao}
                  />
                </Box>
              </Fab>
            </Grid>

            <Grid item ml={1} flex={1} container paddingX={1}>
              <Grid item xs={12} sm={6} md={5} container display="flex" flexDirection="column">
                <Box display="flex">
                  <Typography variant="body1" color={Colors.gray1} lineHeight={1.5}>
                    {transacao.tipoTransacaoDescricao}
                  </Typography>
                </Box>

                <Typography variant="caption" color={Colors.gray1}>
                  Autorização: {transacao.numeroAutorizacao}
                </Typography>
              </Grid>
              <Grid item display="flex" flex={1} alignItems="center" gap={1} mt={{ xs: 1, sm: 0 }}>
                {origemDestinoCpfCnpj && (
                  <Box>
                    <Typography variant="body1" color={Colors.gray1} lineHeight={1.5}>
                      {transacao.origemDestinoNome}
                    </Typography>
                    <Typography variant="caption" color={Colors.gray1}>
                      {origemDestinoCpfCnpj.length > 11 ? 'CNPJ: ' : 'CPF: '}
                      <CpfCnpjFormatter value={origemDestinoCpfCnpj} />
                    </Typography>
                  </Box>
                )}
              </Grid>
              <Grid
                item
                container
                xs={12}
                mt={{ xs: 1, md: 0 }}
                md={origemDestinoCpfCnpj ? 4 : 6}
                justifyContent="flex-end"
              >
                {(transacao.temBilhetes || (transacao.bilhetes?.length ?? 0) > 0) && (
                  <Grid item display="flex" alignItems="center" mr={2}>
                    <Tooltip title="Bilhete">
                      <Box sx={{ cursor: 'pointer' }} onClick={onClick}>
                        <Icon path={mdiTicketConfirmation} size={1} color={Colors.primary.main} />
                      </Box>
                    </Tooltip>
                  </Grid>
                )}
                {transacao.tipoTransacaoDescricao == 'Saldo Adicionado' && (
                  <Grid item display="flex" alignItems="center" mr={2}>
                    <Box sx={{ cursor: 'pointer' }} onClick={navigateToRecibo}>
                      <Tooltip title="Ver recibo">
                        <Icon path={mdiPrinterPosOutline} size={1} color={Colors.primary.main} />
                      </Tooltip>
                    </Box>
                  </Grid>
                )}

                <Grid
                  item
                  flexShrink={1}
                  display="flex"
                  flexDirection="column"
                  alignItems={{ md: 'flex-end' }}
                >
                  <Typography
                    fontWeight="bold"
                    color={getCurrencyColor(transacao.tipo)}
                    sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}
                  >
                    <Currency value={transacao.valor} />
                  </Typography>

                  <Typography
                    variant="caption"
                    color={Colors.gray1}
                    textAlign={{ md: 'right' }}
                    sx={{ textAlignLast: { md: 'right' } }}
                    variantMapping={{ caption: 'p' }}
                  >
                    {format(new Date(transacao.dataHora), 'dd/MM/yyyy HH:mm')}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  }
);

CardTransacao.displayName = 'CardTransacao';
export default CardTransacao;
